import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
  Textarea,
  useRadioGroup,
} from '@chakra-ui/react';
import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import CloudflareTurnstile from '@/app/_components/cloudflare-turnstile/CloudflareTurnstile';
import CustomRadioButton from '@/app/_components/custom-radio-button/CustomRadioButton';
import {
  validateFormEmailField,
  validateFormNameFiled,
  validatePhoneNumberField,
} from '@/constants/validation.constants';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { BrandAndRangesType } from '@/services/filters/new-filters.service';
import { getNewCarSpec } from '@/services/NewCarsService.service';
import {
  NewCar,
  SpecificationsPricesType,
} from '@/types/new-cars/new-cars.types';
import { VehicleType } from '@/types/used-cars/used-cars.types';

import FormStyles from '../../../custom-form-control.module.scss';
import { DealersSelectNewCars } from '../dealers-select/dealers-select';
import { LocationButtonNewCars } from '../location-button/location-button';
import NewCarSelect from '../newCar-select/newCar-select';
import UsedCarSelect from '../usedCar-select/usedCar-select';
import { VariantsSelectNewCars } from '../variant-select/variant-select';

import Styles from './enquire-form.module.scss';

const Location: React.FC<{ onSelect: (value: boolean) => void }> = ({
  onSelect,
}) => {
  const handleChange = (value: string) => {
    onSelect(value === 'Your Place');
  };

  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue: 'Our Place',
    onChange: handleChange,
  });

  return (
    <Box {...getRootProps()}>
      <Flex gap={'10px'}>
        <CustomRadioButton
          radioValue={'Our Place'}
          {...getRadioProps({ value: 'Our Place' })}
        />
        <CustomRadioButton
          radioValue={'Your Place'}
          {...getRadioProps({ value: 'Your Place' })}
        />
      </Flex>
    </Box>
  );
};

const FinanceApply: React.FC<{ onSelect: (value: boolean) => void }> = ({
  onSelect,
}) => {
  const handleChange = (value: string) => {
    onSelect(value === 'Yes');
  };

  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue: 'Yes',
    onChange: handleChange,
  });

  return (
    <Box {...getRootProps()}>
      <Flex gap={'10px'}>
        <CustomRadioButton
          radioValue={'Yes'}
          {...getRadioProps({ value: 'Yes' })}
        />
        <CustomRadioButton
          radioValue={'No'}
          {...getRadioProps({ value: 'No' })}
        />
      </Flex>
    </Box>
  );
};

const TradeInCar: React.FC<{
  onSelect: (value: boolean) => void;
  defaultValue: string;
}> = ({ onSelect, defaultValue }) => {
  const handleChange = (value: string) => {
    onSelect(value === 'Yes');
  };

  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue,
    onChange: handleChange,
  });

  return (
    <Box {...getRootProps()}>
      <Flex gap={'10px'}>
        <CustomRadioButton
          radioValue={'Yes'}
          {...getRadioProps({ value: 'Yes' })}
        />
        <CustomRadioButton
          radioValue={'No'}
          {...getRadioProps({ value: 'No' })}
        />
      </Flex>
    </Box>
  );
};

export const EnquireFormNewCars: React.FC<{
  onSubmitForm: (values: any) => void;
  selectedVariant?: SpecificationsPricesType;
  setSelectedVariant: (variant: SpecificationsPricesType | null) => void;
  selectedNewCarId?: number | null;
  setSelectedNewCarId: (id: number) => void;
  availableDealershipIds?: string[];
  disableSelectCar?: boolean;
  disableSelectVariant?: boolean;
  isLoading: boolean;
  newCars?: NewCar[];
  brandsAndRanges: BrandAndRangesType;
}> = ({
  onSubmitForm,
  setSelectedVariant,
  selectedVariant,
  selectedNewCarId,
  setSelectedNewCarId,
  availableDealershipIds,
  disableSelectCar,
  disableSelectVariant,
  isLoading,
  brandsAndRanges,
  newCars: initialNewCars,
}) => {
  const [newCars, setNewCars] = useState<NewCar[] | undefined>(initialNewCars);
  const [isTurnstileLoading, setIsTurnstileLoading] = useState(false);
  const [isFetchingVariants, setIsFetchingVariants] = useState(false);
  const selectedCar = newCars?.find(({ id }) => id === selectedNewCarId);

  const { leadType, leadFormName, formItem } = useFloatingCTA();

  const carVariants = selectedCar?.specification_prices || [];

  useEffect(() => {
    const fetchVariants = async () => {
      if (
        selectedNewCarId &&
        (!carVariants || carVariants.length === 0) &&
        !isFetchingVariants
      ) {
        try {
          setIsFetchingVariants(true);
          const { data: fetchedCarSpec } =
            await getNewCarSpec(selectedNewCarId);

          const hasValidSpecifications =
            fetchedCarSpec?.specification_prices &&
            fetchedCarSpec.specification_prices.length > 0;

          const updatedCars = newCars?.map((car) => {
            if (car.id === selectedNewCarId) {
              return {
                ...car,
                specification_prices: hasValidSpecifications
                  ? fetchedCarSpec.specification_prices
                  : car.specification_prices,
              };
            }
            return car;
          });

          if (!hasValidSpecifications) {
            return;
          } else {
            setNewCars(updatedCars);
          }
        } catch (error) {
          console.error('Failed to fetch variants:', error);
        } finally {
          setIsFetchingVariants(false);
        }
      }
    };

    fetchVariants();
  }, [selectedNewCarId]);
  const defaultMessage =
    formItem?.type === 'used-car'
      ? `I would like to make an enquiry about ${formItem.usedCar.title}`
      : selectedCar
        ? selectedVariant
          ? `I would like to make an enquiry about ${selectedCar?.main_title} - ${selectedVariant.title}`
          : `I would like to make an enquiry about ${selectedCar?.main_title}`
        : ``;

  const defaultDealershipId =
    formItem?.type === 'used-car' ? formItem.usedCar.dealership_id.id : null;

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'all',
    defaultValues: {
      user_name: '',
      email: '',
      phone_number: '',
      employee_number: '',
      message: defaultMessage,
      branch_name: defaultDealershipId,
      car_model: selectedNewCarId,
      location_at_contact: false,
      finance: true,
      trade_in: leadType === 'trade-in',
      'cf-turnstile-response': undefined as undefined | string,
    },
  });

  return (
    <Box gap={2}>
      <Stack
        as="form"
        onSubmit={handleSubmit(onSubmitForm)}
        className={cn(Styles.enquireNowForm, Styles.enquireFormWrapper)}
        gap={8}
        name={leadFormName}
        id={leadFormName}
      >
        {formItem?.type !== 'used-car' &&
          newCars &&
          newCars?.length > 0 &&
          !(
            formItem?.type === 'special' &&
            formItem.special.vehicle_type === VehicleType.Used
          ) && (
            <Stack gap={4} mb={-4}>
              <FormControl id="car_model" isInvalid={!!errors.car_model}>
                <NewCarSelect
                  newCars={newCars}
                  name="car_model"
                  selectedNewCarId={selectedNewCarId}
                  setSelectedNewCarId={(id) => {
                    setSelectedNewCarId(id);
                    setSelectedVariant(null);
                  }}
                  register={register}
                  disabled={disableSelectCar}
                  required={true}
                />
                <FormErrorMessage>
                  {errors.car_model && errors.car_model.message}
                </FormErrorMessage>
              </FormControl>
              <VariantsSelectNewCars
                variants={carVariants}
                setSelectedVariant={setSelectedVariant}
                selectedVariant={selectedVariant}
                disabled={selectedNewCarId ? disableSelectVariant : true}
              />
            </Stack>
          )}

        {formItem?.type === 'special' &&
          formItem.special.vehicle_type === VehicleType.Used && (
            <Stack gap={4} mb={-4}>
              <UsedCarSelect
                errors={errors}
                register={register}
                setValue={setValue}
                brandsAndRanges={brandsAndRanges}
                selectedMake={formItem?.special?.brand?.name}
                disableMake={!!formItem?.special?.brand?.name}
                selectedModel={formItem?.special?.range?.name}
                disableModel={!!formItem?.special?.range?.name}
                selectedVariant={formItem?.special?.model?.name}
                defaultVariants={
                  formItem?.special?.model ? [formItem.special.model] : []
                }
                disableVariant={!!formItem?.special?.model?.id}
              />
            </Stack>
          )}

        {formItem?.type === 'used-car' && (
          <Box data-cy="used-car-enquiry-title">{formItem.usedCar.title}</Box>
        )}

        <FormControl id="user_name" isInvalid={!!errors.user_name}>
          <Box className={FormStyles.floatingForm}>
            <Input
              {...register('user_name', {
                validate: validateFormNameFiled,
              })}
              name="user_name"
              className={FormStyles.formInput}
              variant="flushed"
              placeholder=" "
            />
            <FormLabel className={FormStyles.formLabel}>Name*</FormLabel>
          </Box>
          <FormErrorMessage>
            {errors.user_name && errors.user_name.message}
          </FormErrorMessage>
        </FormControl>

        <FormControl id="email" isInvalid={!!errors.email}>
          <Box className={FormStyles.floatingForm}>
            <Input
              {...register('email', {
                required: 'Please input your Email!',
                validate: validateFormEmailField,
              })}
              name="email"
              className={FormStyles.formInput}
              variant="flushed"
              placeholder=" "
              type="email"
            />
            <FormLabel className={FormStyles.formLabel}>E-mail*</FormLabel>
          </Box>
          <FormErrorMessage>
            {errors.email && errors.email.message}
          </FormErrorMessage>
        </FormControl>

        <FormControl id="phone_number" isInvalid={!!errors.phone_number}>
          <Box className={FormStyles.floatingForm}>
            <Input
              {...register('phone_number', {
                required: 'Please input your Phone number!',
                validate: validatePhoneNumberField,
              })}
              name="phone_number"
              className={FormStyles.formInput}
              variant="flushed"
              placeholder=" "
            />
            <FormLabel className={FormStyles.formLabel}>
              Mobile Number*
            </FormLabel>
          </Box>
          <FormErrorMessage>
            {errors.phone_number && errors.phone_number.message}
          </FormErrorMessage>
        </FormControl>

        {leadType === 'corporate' && (
          <FormControl id="employee_number" isInvalid={!!errors.employee_number}>
            <Box className={FormStyles.floatingForm}>
              <Input
                {...register('employee_number', {
                  required: 'Please input your Employee Number!',
                  pattern: {
                    value: /^[A-Za-z0-9]+$/,
                    message:
                      'Employee Number must contain only letters and digits (no spaces or symbols).',
                  },
                })}
                name="employee_number"
                className={FormStyles.formInput}
                variant="flushed"
                placeholder=" "
              />
              <FormLabel className={FormStyles.formLabel}>
                Employee Number*
              </FormLabel>
            </Box>
            <FormErrorMessage>
              {errors.employee_number && errors.employee_number.message}
            </FormErrorMessage>
          </FormControl>
        )}

        <FormControl id="message" isInvalid={!!errors.message}>
          <Box className={FormStyles.floatingForm}>
            <Textarea
              {...register('message', {
                required: 'Please input Message!',
              })}
              rows={2}
              name="message"
              className={FormStyles.formInput}
              variant="flushed"
              placeholder=" "
            />
            <FormLabel className={FormStyles.formLabel}>Message*</FormLabel>
          </Box>
          <FormErrorMessage>
            {errors.message && errors.message.message}
          </FormErrorMessage>
        </FormControl>

        {formItem?.type !== 'used-car' && leadType !== 'fleet' && leadType !== 'corporate' && (
          <LocationButtonNewCars
            availableDealershipIds={availableDealershipIds}
          />
        )}

        {leadType !== 'fleet' && formItem?.type !== 'used-car' && leadType !== 'corporate' && (
          <FormControl id="branch_name" isInvalid={!!errors.branch_name}>
            <DealersSelectNewCars
              name="branch_name"
              register={register}
              onChange={(val) => setValue('branch_name', val)}
              required={true}
              availableDealershipIds={availableDealershipIds}
            />
            <FormErrorMessage>
              {errors.branch_name && errors.branch_name.message?.toString()}
            </FormErrorMessage>
          </FormControl>
        )}

        {formItem?.type === 'used-car' && leadType !== 'corporate' && (
          <Box data-cy="used-car-enquiry-title">
            {formItem.usedCar.dealership_id.name}
          </Box>
        )}

        {leadType === 'test-drive' && (
          <FormControl id="location_at_contact">
            <FormLabel className={FormStyles.formLabel}>
              Where would you prefer to meet?
            </FormLabel>
            <Location
              onSelect={(value: boolean) =>
                setValue('location_at_contact', value)
              }
            />
          </FormControl>
        )}

        {leadType !== 'fleet' && leadType !== 'parts' && leadType !== 'corporate' && (
          <>
            <FormControl id="finance">
              <FormLabel className={FormStyles.formLabel}>
                I would like to apply for finance
              </FormLabel>
              <FinanceApply
                onSelect={(value: boolean) => setValue('finance', value)}
              />
            </FormControl>
            <FormControl id="trade_in">
              <FormLabel className={FormStyles.formLabel}>
                I would like to trade in car
              </FormLabel>
              <TradeInCar
                defaultValue={leadType === 'trade-in' ? 'Yes' : 'No'}
                onSelect={(value: boolean) => setValue('trade_in', value)}
              />
            </FormControl>
          </>
        )}

        <Button
          type="submit"
          isLoading={isLoading}
          isDisabled={isTurnstileLoading}
          loadingText="Submitting"
          data-cy="send-enquiry-button"
          className={FormStyles.btn}
        >
          <span>Call me back!</span>
        </Button>

        <CloudflareTurnstile
          onVerify={(token) => {
            setValue('cf-turnstile-response', token);
            setIsTurnstileLoading(false);
          }}
          onError={() => {
            setValue('cf-turnstile-response', '');
            setIsTurnstileLoading(true);
          }}
          onExpire={() => {
            setValue('cf-turnstile-response', '');
            setIsTurnstileLoading(true);
          }}
          onLoad={() => {
            setValue('cf-turnstile-response', '');
            setIsTurnstileLoading(true);
          }}
        />
      </Stack>
    </Box>
  );
};
